/**
 * Form measures for the panel-less public pages (NG-8572, widened in NG-8898).
 *
 * These pages used to size their form as a percentage of the right-hand column, which was itself
 * half the viewport — so `width: 70%` read as ~35% of the screen. Removing the left branding panel
 * made that column full width and every form doubled, stretching a single input across a 1440px
 * display. A fixed max-width centred in the column is the honest fix: it holds a readable measure
 * at any viewport instead of re-deriving a ladder of breakpoints per page.
 *
 * NG-8572 chose 480 from the design system's 60–80 characters-per-line rule (~430px of content).
 * That rule is for running prose, and these cards hold none: they are a logo, a heading, short
 * labelled inputs and full-width buttons. At 480 the card read as undersized on a desktop — a
 * quarter of a 1920px screen — so NG-8898 widened both measures by ~25%. Fields and buttons do not
 * become harder to scan at 600, and the descriptions/helper copy that does wrap stays well inside
 * 80 characters at the 15–16px sizes used here.
 *
 * Neither measure affects phones: the wrapper is `width: 100%` and the max-width only binds once
 * the viewport is wider than it, so below ~600px the card is exactly as wide as it was before.
 */

/** Single-column forms: login, register wizard, forgot/reset password, MFA code entry. */
export const PUBLIC_FORM_MAX_WIDTH = 600;

/** Two-column forms — patient registration pairs its fields (first/last, email/birth date, ...). */
export const WIDE_PUBLIC_FORM_MAX_WIDTH = 950;

/** Keeps fields off the screen edge on small phones, where the max-width never binds. */
const INLINE_PADDING = "1.5rem";

/**
 * The shared form-wrapper box. Spread this and add per-page vertical padding.
 *
 * `boxSizing` is not optional here: no CssBaseline/border-box reset is applied app-wide (see the
 * note in VerifyStyles), so under the default content-box the inline padding is added ON TOP of
 * `width: 100%` and the form overflows its column by 48px on a phone.
 */
export const publicFormWrapper = (maxWidth: number) =>
    ({
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth,
        marginInline: "auto",
        paddingInline: INLINE_PADDING,
        boxSizing: "border-box",
    }) as const;
